import Flex from "../../../core/components/Flex";
import {Button, List, Modal, Select, Tooltip} from "antd";
import {t} from "i18next";
import {useDataGrid} from "../../../core/components/data-grid/DataGridContext";
import Colors from "../../../core/style/Colors";
import {useEffect, useState} from "react";
import ApiService from "../../../core/service/api-service";
import {PERMISSIONS, useUser} from "../../../core/context/UserContext";
import {SwapOutlined} from "@ant-design/icons";
import { LocalStorageService } from "../../../core/service/local-storage-service";

const colors = [
    {color: '#ced6e0', text: 'DELETED-ROW'},
    {color: '#55efc4', text: 'REPORT-CONFIRMED'},
    {color: '#dff9fb', text: 'REPORT-NOT-CONFIRMED-ROW'},
    {color: Colors.selectedRow, text: 'SELECTED-ROW'},
]

export default function StudyRequestGridTools({studyId})
{
    const {refreshDataGrid, selectedRows} = useDataGrid();
    const {hasPermission} = useUser()
    const [requestTypes, setRequestTypes] = useState([])
    const [selReqs, setSelReqs] = useState([])
    const [isMoveModalVisible, setIsMoveModalVisible] = useState(false)
    const [targetStudies, setTargetStudies] = useState([])
    const [selectedTargetStudyId, setSelectedTargetStudyId] = useState(null)

    const getRequestTypes = async () => {
        const res = await ApiService.RequestType.GetAvailableRequestTypes(studyId);
        if(res.success) {
            setRequestTypes(res.data.map(req => ({
                ...req,
                label: req.name,
                value: req.id
            })))
        }
    }

    useEffect(() => {
        hasPermission(PERMISSIONS.STUDY_REQUEST_SET) && getRequestTypes()
    }, [])

    const openMoveRequestModal = async () => {
        const res = await ApiService.Request.GetTargetStudiesForMoveRequest(studyId);
        if(res.success) {
            setTargetStudies(res.data || [])
            setIsMoveModalVisible(true)
        }
    }

    const closeMoveRequestModal = () => {
        setIsMoveModalVisible(false)
        setSelectedTargetStudyId(null)
        setTargetStudies([])
    }

    const handleMoveRequest = async () => {
        if(!selectedTargetStudyId) {
            return;
        }
        
        const requestIdList = selectedRows.map(row => row.id);
        
        const res = await ApiService.Request.MoveRequest(studyId, {
            targetStudyId: selectedTargetStudyId,
            requestIdList: requestIdList
        });

        if(res.success) {
            closeMoveRequestModal()
            refreshDataGrid()
        }
    }

    const addRequest = async () => {
        const res = await ApiService.Request.AddRequests(studyId, {
            requestTypeIdList: selReqs
        });

        if(res.success) {
            refreshDataGrid()
            setSelReqs([])
        }
    }

    return (
        <>
            <div style={{display: 'flex', alignItems: 'start', flexWrap: 'wrap', gap: 8}}>
                <div>
                    <Flex flexDirection={'row'}>
                        {
                            colors.map(color => {
                                return (
                                    <div key={color.text}>
                                        <Tooltip title={t(color.text)}>
                                            <div style={{width: 16, height: 16, backgroundColor: color.color, marginRight: 4}} />
                                        </Tooltip>
                                    </div>
                                )
                            })
                        }
                    </Flex>
                </div>
                {
                    hasPermission(PERMISSIONS.STUDY_REQUEST_SET) && (
                        <>
                            <Select
                                size={'small'}
                                value={selReqs}
                                onChange={(values) => setSelReqs(values)}
                                mode={'multiple'}
                                options={[...requestTypes].sort((a, b) => a.label.localeCompare(b.label, LocalStorageService.getLanguage()))}
                                showSearch={true}
                                allowClear={true}
                                placeholder={t('Add Request & Anamnez')}
                                style={{width: 300}}
                                filterOption={(input, option) => {
                                    return (
                                        option.name.toLocaleLowerCase().includes(input.toLocaleLowerCase())
                                        || option.options?.find(x => x.label.toLocaleLowerCase().includes(input.toLocaleLowerCase()))
                                    )
                                }}
                            />
                            <Button
                                size={'small'}
                                type={'primary'}
                                onClick={addRequest}
                            >
                                {t('Add')}
                            </Button>
                            <Tooltip title={t('Move Selected Requests to Another Study')}>
                                <Button
                                    size={'small'}
                                    icon={<SwapOutlined />}
                                    onClick={openMoveRequestModal}
                                    disabled={!selectedRows || selectedRows.length === 0}
                                >
                                    {t('Move Request')}
                                </Button>
                            </Tooltip>
                        </>
                    )
                }
            </div>

            <Modal
                title={t('Move Request to Another Study')}
                open={isMoveModalVisible}
                onCancel={closeMoveRequestModal}
                onOk={handleMoveRequest}
                okText={t('Move')}
                cancelText={t('Cancel')}
                okButtonProps={{disabled: !selectedTargetStudyId}}
                width={800}
            >
                <div style={{marginBottom: 16}}>
                    <strong>{t('Selected Requests')}: </strong>
                    {
                        selectedRows?.map(r => (
                            <div>
                                {r?.requestTypeName}
                            </div>
                        ))
                    }
                </div>
                <div style={{marginBottom: 16}}>
                    <strong>{t('Select Target Study')}:</strong>
                </div>
                <List
                    bordered
                    dataSource={targetStudies}
                    renderItem={(study) => (
                        <List.Item
                            onClick={() => setSelectedTargetStudyId(study.id)}
                            style={{
                                cursor: 'pointer',
                                backgroundColor: selectedTargetStudyId === study.id ? Colors.selectedRow : 'white'
                            }}
                        >
                            <div style={{width: '100%'}}>
                                <div><strong>{t('Patient Name')}:</strong> {study?.patient?.name}</div>
                                <div><strong>{t('Patient Id')}:</strong> {study?.patient?.dicomPatientId}</div>
                                <div><strong>{t('Study Description')}:</strong> {study?.studyDescription}</div>
                                <div><strong>{t('Accession Number')}:</strong> {study.accessionNumber}</div>
                                <div><strong>{t('Study Date')}:</strong> {new Date(study.studyDateTime)?.toLocaleDateString()}</div>
                            </div>
                        </List.Item>
                    )}
                    style={{maxHeight: 400, overflow: 'auto'}}
                />
            </Modal>
        </>
    )
}